/**
 * Portfolio Analytics (ex-Controle de Posições, renomeada em 2026-10-26; migrada do
 * Streamlit, equivalência em backend/tests_paginas/test_controle.py). A URL continua
 * `controle-de-posicoes` para não quebrar links salvos.
 *
 * Duas origens, como no legado: carteiras sob gestão (na URL: carteiras e horizonte do
 * fluxo; filtradas por `portfolios_visiveis` no servidor) ou carteira externa (arquivo
 * CSV/Excel enviado e lido em memória no servidor; não fica na URL).
 */
import {
  Accordion,
  Alert,
  Anchor,
  Button,
  Checkbox,
  FileInput,
  Group,
  Loader,
  MultiSelect,
  SegmentedControl,
  SimpleGrid,
  Stack,
  Table,
  Tabs,
  Text,
  TextInput,
} from '@mantine/core';
import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { ErroApi, apiGet, apiPostForm, montarUrl, type Envelope } from '../../api/cliente';
import { AvisosApi } from '../../components/AvisosApi';
import {
  BarraFiltros,
  CampoData,
  CampoOpcoes,
  GrupoFiltros,
  LARGURA,
} from '../../components/Filtros';
import { Grafico, type GraficoApi } from '../../components/Grafico';
import { Kpi, type KpiApi } from '../../components/Kpi';
import { CabecalhoPagina, Pagina, Secao } from '../../components/Layout';
import { TabelaDados, type TabelaApi } from '../../components/TabelaDados';
import { formatarData } from '../../lib/formatos';
import { useFiltrosUrl } from '../../lib/url-state';
import { PerformanceCarteira } from './PerformanceCarteira';

const CAMINHO = '/carteiras-administradas/controle-de-posicoes';
const HORIZONTES = [
  { value: '6', label: '6 meses' },
  { value: '12', label: '12 meses' },
  { value: '24', label: '24 meses' },
  { value: '36', label: '36 meses' },
];

type Bloco = { info?: string; aviso?: string; sucesso?: string };
/** Memória de cálculo da duration da carteira (textos já formatados no backend). */
interface MemoriaDuration {
  titulo: string;
  formula: string;
  explicacao: string;
  conta: string;
  classes: {
    classe: string;
    ativos: string;
    saldo: string;
    peso: string;
    duration: string;
    contribuicao: string;
  }[];
  fontes: { fonte: string; regra: string; ativos: string; saldo: string; pct: string }[];
  notas: string[];
}
interface Abas {
  alocacao: { atual: GraficoApi; alvo: Bloco & { grafico?: GraficoApi } };
  hierarquica: GraficoApi[];
  emissores: GraficoApi;
  instrumentos: GraficoApi;
  custodiantes: GraficoApi;
  vencimentos: {
    legenda: string[];
    memoria?: MemoriaDuration;
    aviso?: string;
    tabela: TabelaApi;
    histogramas: { titulo: string; grafico?: GraficoApi; info?: string }[];
  };
  fluxo: {
    legenda: string;
    avisos: string[];
    sem_cronograma?: TabelaApi;
    info?: string;
    kpis?: KpiApi[];
    grafico?: GraficoApi;
    eventos?: TabelaApi;
    por_ativo?: TabelaApi;
  };
  fgc: Bloco & { tabela?: TabelaApi; grafico?: GraficoApi };
  alertas: Bloco & { kpis?: KpiApi[]; tabela?: TabelaApi };
}
export interface DadosControle {
  opcoes?: { carteiras: string[] };
  info?: string;
  aviso?: string;
  match?: {
    resumo: { matched: number; ambiguous: number; unmatched: number; total: number };
    tabela: TabelaApi;
  };
  titulo?: string;
  saldo_total?: string;
  data_posicao?: string;
  contas?: { titulo: string; contas?: string; custodiantes?: string; texto?: string }[];
  composicao?: TabelaApi;
  politica?: (Bloco & { tabela?: TabelaApi }) | null;
  kpis?: KpiApi[];
  abas?: Abas;
}

function Mensagem({ b }: { b: Bloco }) {
  if (b.info) return <Alert color="blue">{b.info}</Alert>;
  if (b.aviso) return <Alert color="yellow">{b.aviso}</Alert>;
  if (b.sucesso) return <Alert color="green">{b.sucesso}</Alert>;
  return null;
}

/** Recolhida por padrão: o resultado já está na legenda; o detalhe abre ao clicar. */
function MemoriaCalculo({ m }: { m: MemoriaDuration }) {
  const direita = { textAlign: 'right' } as const;
  return (
    <Accordion variant="contained">
      <Accordion.Item value="memoria">
        <Accordion.Control>
          <Text size="sm">{m.titulo}</Text>
        </Accordion.Control>
        <Accordion.Panel>
          <Stack gap="md">
            <Text size="xs" c="dimmed">
              {m.explicacao}
            </Text>
            <Stack gap={2}>
              <Text size="sm" fw={500}>
                {m.formula}
              </Text>
              <Text size="sm" ff="monospace">
                {m.conta}
              </Text>
            </Stack>
            <SimpleGrid cols={{ base: 1, xl: 2 }} spacing="lg">
              <Table withTableBorder verticalSpacing={4} fz="sm">
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Classe</Table.Th>
                    <Table.Th style={direita}>Títulos</Table.Th>
                    <Table.Th style={direita}>Saldo</Table.Th>
                    <Table.Th style={direita}>% (peso)</Table.Th>
                    <Table.Th style={direita}>Duration (anos)</Table.Th>
                    <Table.Th style={direita}>Contribuição (anos)</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {m.classes.map((c) => (
                    <Table.Tr key={c.classe} fw={c.classe === 'Carteira' ? 500 : undefined}>
                      <Table.Td>{c.classe}</Table.Td>
                      <Table.Td style={direita}>{c.ativos}</Table.Td>
                      <Table.Td style={direita}>{c.saldo}</Table.Td>
                      <Table.Td style={direita}>{c.peso}</Table.Td>
                      <Table.Td style={direita}>{c.duration}</Table.Td>
                      <Table.Td style={direita}>{c.contribuicao}</Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
              <Table withTableBorder verticalSpacing={4} fz="sm">
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Fonte da duration</Table.Th>
                    <Table.Th>Regra</Table.Th>
                    <Table.Th style={direita}>Títulos</Table.Th>
                    <Table.Th style={direita}>% do saldo</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {m.fontes.map((f) => (
                    <Table.Tr key={f.fonte}>
                      <Table.Td style={{ whiteSpace: 'nowrap' }}>{f.fonte}</Table.Td>
                      <Table.Td c="dimmed">{f.regra}</Table.Td>
                      <Table.Td style={direita}>{f.ativos}</Table.Td>
                      <Table.Td style={direita} title={f.saldo}>
                        {f.pct}
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </SimpleGrid>
            {m.notas.length > 0 && (
              <Stack gap={2}>
                {m.notas.map((n) => (
                  <Text key={n} size="xs" c="dimmed">
                    {n}
                  </Text>
                ))}
              </Stack>
            )}
          </Stack>
        </Accordion.Panel>
      </Accordion.Item>
    </Accordion>
  );
}

function mensagemErro(erro: unknown, padrao: string) {
  return erro instanceof ErroApi ? erro.message : padrao;
}

/** Só nas carteiras sob gestão: aba Performance (Warren One) e aba aberta na URL. */
interface ComPerformance {
  carteiras: string[];
  aba: string;
  aoMudarAba: (aba: string) => void;
  periodo: string;
  aoMudarPeriodo: (periodo: string) => void;
}

function Resultado({
  d,
  horizonte,
  aoMudarHorizonte,
  performance,
}: {
  d: DadosControle;
  horizonte: number;
  aoMudarHorizonte: (h: number) => void;
  performance?: ComPerformance;
}) {
  const a = d.abas!;
  return (
    <>
      <Secao
        titulo={d.titulo ?? ''}
        legenda={`Saldo total: ${d.saldo_total} · Posição de ${formatarData(d.data_posicao ?? null)}`}
      >
        <Stack gap={4}>
          {(d.contas ?? []).map((c) => (
            <Text key={c.titulo} size="sm">
              <b>{c.titulo}</b>
              {c.contas && ` · Conta(s): ${c.contas} · Custodiante(s): ${c.custodiantes}`}
              {c.texto && ` · ${c.texto}`}
            </Text>
          ))}
        </Stack>
      </Secao>
      {d.kpis && (
        <SimpleGrid cols={{ base: 2, md: 3, xl: 5 }}>
          {d.kpis.map((k) => (
            <Kpi key={k.rotulo} kpi={k} />
          ))}
        </SimpleGrid>
      )}
      <SimpleGrid cols={{ base: 1, xl: d.politica ? 2 : 1 }} spacing="lg">
        <Secao titulo="Composição completa" legenda="Posição mais recente, por ativo.">
          {d.composicao && <TabelaDados tabela={d.composicao} altura={430} />}
        </Secao>
        {d.politica && (
          <Secao
            titulo="Política de investimentos"
            legenda="Limites mínimo e máximo por classe (% do PL)."
          >
            {d.politica.tabela ? (
              <TabelaDados tabela={d.politica.tabela} alturaAutomatica />
            ) : (
              <Mensagem b={d.politica} />
            )}
          </Secao>
        )}
      </SimpleGrid>
      <Tabs
        defaultValue="alocacao"
        value={performance?.aba}
        onChange={(v) => v && performance?.aoMudarAba(v)}
        keepMounted={false}
        color="dourado"
      >
        <Tabs.List>
          <Tabs.Tab value="alocacao">Alocação atual</Tabs.Tab>
          <Tabs.Tab value="hierarquica">Alocação hierárquica</Tabs.Tab>
          <Tabs.Tab value="emissores">Emissores</Tabs.Tab>
          <Tabs.Tab value="instrumentos">Instrumentos</Tabs.Tab>
          <Tabs.Tab value="custodiantes">Custodiantes</Tabs.Tab>
          <Tabs.Tab value="vencimentos">Vencimentos & duration</Tabs.Tab>
          <Tabs.Tab value="fluxo">Fluxo de caixa</Tabs.Tab>
          <Tabs.Tab value="fgc">Monitor de FGC</Tabs.Tab>
          <Tabs.Tab value="alertas">Alertas</Tabs.Tab>
          {performance && <Tabs.Tab value="performance">Performance</Tabs.Tab>}
        </Tabs.List>
        {performance && (
          <Tabs.Panel value="performance" pt="md">
            <PerformanceCarteira
              carteiras={performance.carteiras}
              periodo={performance.periodo}
              aoMudarPeriodo={performance.aoMudarPeriodo}
            />
          </Tabs.Panel>
        )}
        <Tabs.Panel value="alocacao" pt="md">
          <SimpleGrid cols={{ base: 1, lg: 2 }}>
            <Grafico grafico={a.alocacao.atual} />
            {a.alocacao.alvo.grafico ? (
              <Grafico grafico={a.alocacao.alvo.grafico} />
            ) : (
              <Mensagem b={a.alocacao.alvo} />
            )}
          </SimpleGrid>
        </Tabs.Panel>
        <Tabs.Panel value="hierarquica" pt="md">
          <Stack>
            {a.hierarquica.map((g) => (
              <Grafico key={g.id} grafico={g} />
            ))}
          </Stack>
        </Tabs.Panel>
        <Tabs.Panel value="emissores" pt="md">
          <Grafico grafico={a.emissores} />
        </Tabs.Panel>
        <Tabs.Panel value="instrumentos" pt="md">
          <Grafico grafico={a.instrumentos} />
        </Tabs.Panel>
        <Tabs.Panel value="custodiantes" pt="md">
          <Grafico grafico={a.custodiantes} />
        </Tabs.Panel>
        <Tabs.Panel value="vencimentos" pt="md">
          <Stack>
            {a.vencimentos.legenda.map((l) => (
              <Text key={l} size="sm" c="dimmed">
                {l}
              </Text>
            ))}
            {a.vencimentos.memoria && <MemoriaCalculo m={a.vencimentos.memoria} />}
            {a.vencimentos.aviso && <Alert color="yellow">{a.vencimentos.aviso}</Alert>}
            <SimpleGrid cols={{ base: 1, xl: 2 }}>
              <TabelaDados tabela={a.vencimentos.tabela} altura={420} />
              <Tabs defaultValue="0" keepMounted={false} color="dourado">
                <Tabs.List>
                  {a.vencimentos.histogramas.map((h, i) => (
                    <Tabs.Tab key={h.titulo} value={String(i)}>
                      {h.titulo}
                    </Tabs.Tab>
                  ))}
                </Tabs.List>
                {a.vencimentos.histogramas.map((h, i) => (
                  <Tabs.Panel key={h.titulo} value={String(i)} pt="sm">
                    {h.grafico ? (
                      <Grafico grafico={h.grafico} />
                    ) : (
                      <Alert color="blue">{h.info}</Alert>
                    )}
                  </Tabs.Panel>
                ))}
              </Tabs>
            </SimpleGrid>
          </Stack>
        </Tabs.Panel>
        <Tabs.Panel value="fluxo" pt="md">
          <Stack>
            <Group>
              <Text size="sm" fw={500}>
                Horizonte de projeção
              </Text>
              <SegmentedControl
                data={HORIZONTES}
                value={String(horizonte)}
                onChange={(v) => aoMudarHorizonte(Number(v))}
                color="dourado"
              />
            </Group>
            <Text size="sm" c="dimmed">
              {a.fluxo.legenda}
            </Text>
            {a.fluxo.avisos.map((t) => (
              <Alert key={t} color="yellow">
                {t}
              </Alert>
            ))}
            {a.fluxo.sem_cronograma && (
              <Secao titulo="Ativos sem eventos cadastrados no período">
                <TabelaDados tabela={a.fluxo.sem_cronograma} alturaAutomatica />
              </Secao>
            )}
            {a.fluxo.info && <Alert color="blue">{a.fluxo.info}</Alert>}
            {a.fluxo.kpis && (
              <SimpleGrid cols={{ base: 2, lg: 4 }}>
                {a.fluxo.kpis.map((k) => (
                  <Kpi key={k.rotulo} kpi={k} />
                ))}
              </SimpleGrid>
            )}
            {a.fluxo.grafico && <Grafico grafico={a.fluxo.grafico} />}
            {a.fluxo.eventos && a.fluxo.por_ativo && (
              <SimpleGrid cols={{ base: 1, xl: 2 }}>
                <Secao titulo="Eventos">
                  <TabelaDados tabela={a.fluxo.eventos} altura={360} />
                </Secao>
                <Secao titulo="Total por ativo no período">
                  <TabelaDados tabela={a.fluxo.por_ativo} altura={360} />
                </Secao>
              </SimpleGrid>
            )}
          </Stack>
        </Tabs.Panel>
        <Tabs.Panel value="fgc" pt="md">
          {a.fgc.tabela && a.fgc.grafico ? (
            <SimpleGrid cols={{ base: 1, xl: 2 }}>
              <TabelaDados tabela={a.fgc.tabela} altura={360} />
              <Grafico grafico={a.fgc.grafico} />
            </SimpleGrid>
          ) : (
            <Mensagem b={a.fgc} />
          )}
        </Tabs.Panel>
        <Tabs.Panel value="alertas" pt="md">
          {a.alertas.tabela ? (
            <Stack>
              <SimpleGrid cols={{ base: 2, lg: 4 }}>
                {(a.alertas.kpis ?? []).map((k) => (
                  <Kpi key={k.rotulo} kpi={k} />
                ))}
              </SimpleGrid>
              <TabelaDados tabela={a.alertas.tabela} alturaAutomatica />
            </Stack>
          ) : (
            <Mensagem b={a.alertas} />
          )}
        </Tabs.Panel>
      </Tabs>
    </>
  );
}

function Externa() {
  const [nome, setNome] = useState('EXTERNA');
  const [data, setData] = useState(() => new Date().toISOString().slice(0, 10));
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [parcial, setParcial] = useState(false);
  const [horizonte, setHorizonte] = useState(12);
  const envio = useMutation({
    mutationFn: (h: number) => {
      const f = new FormData();
      f.append('arquivo', arquivo!);
      f.append('nome', nome);
      f.append('data', data);
      f.append('parcial', String(parcial));
      f.append('horizonte', String(h));
      return apiPostForm<Envelope<DadosControle>>(`${CAMINHO}/externa`, f);
    },
  });
  const d = envio.data?.data;
  const r = d?.match?.resumo;
  return (
    <>
      <BarraFiltros carregando={envio.isPending}>
        <GrupoFiltros titulo="Carteira externa">
          <TextInput
            label="Nome da carteira"
            value={nome}
            onChange={(e) => setNome(e.currentTarget.value)}
            w={LARGURA.texto}
          />
          <CampoData rotulo="Data da posição" valor={data} aoMudar={setData} />
          <FileInput
            label="Arquivo de posições"
            description="CSV ou Excel; Ativo e Saldo obrigatórios"
            placeholder="Escolher arquivo"
            accept=".csv,.xlsx,.xls"
            value={arquivo}
            onChange={setArquivo}
            clearable
            w={LARGURA.selecao + 40}
          />
          <Stack gap={6} mt={22}>
            <Checkbox
              label="Analisar apenas ativos com match"
              checked={parcial}
              onChange={(e) => setParcial(e.currentTarget.checked)}
            />
            <Anchor size="sm" href={montarUrl(`${CAMINHO}/externa/template`)}>
              Baixar template
            </Anchor>
          </Stack>
          <Button
            mt={25}
            color="dourado"
            disabled={!arquivo}
            onClick={() => envio.mutate(horizonte)}
          >
            Analisar
          </Button>
        </GrupoFiltros>
      </BarraFiltros>
      {!d && !envio.isPending && !envio.isError && (
        <Alert color="blue">
          Envie um arquivo CSV/Excel com as colunas <b>Ativo</b> e <b>Saldo</b> (use o template). O
          arquivo é lido em memória e não fica guardado.
        </Alert>
      )}
      {envio.isError && (
        <Alert color="red">{mensagemErro(envio.error, 'Não foi possível ler o arquivo.')}</Alert>
      )}
      {d?.match && r && (
        <Secao
          titulo="Qualidade do match"
          legenda={`${r.total} ativos: ${r.matched} com match, ${r.ambiguous} ambíguos, ${r.unmatched} sem match no cadastro do Fibery.`}
        >
          <TabelaDados tabela={d.match.tabela} altura={r.ambiguous + r.unmatched > 0 ? 300 : 200} />
        </Secao>
      )}
      {d?.aviso && <Alert color="yellow">{d.aviso}</Alert>}
      {d?.abas && (
        <Resultado
          d={d}
          horizonte={horizonte}
          aoMudarHorizonte={(h) => {
            setHorizonte(h);
            envio.mutate(h);
          }}
        />
      )}
    </>
  );
}

export function ControlePosicoes() {
  const [filtros, definir] = useFiltrosUrl({
    origem: 'gestao',
    carteiras: '',
    horizonte: 12,
    aba: 'alocacao',
    periodo: '12m',
  });
  const carteiras = filtros.carteiras ? filtros.carteiras.split(',') : [];
  const externa = filtros.origem === 'externa';
  const lista = useQuery({
    queryKey: ['carteiras.visiveis'],
    queryFn: ({ signal }) =>
      apiGet<Envelope<{ carteiras: string[] }>>('/carteiras-administradas/carteiras', {}, signal),
    staleTime: 60 * 60 * 1000,
    enabled: !externa,
  });
  const consulta = useQuery({
    queryKey: ['carteiras.controle', carteiras, filtros.horizonte],
    queryFn: ({ signal }) =>
      apiGet<Envelope<DadosControle>>(CAMINHO, { carteiras, horizonte: filtros.horizonte }, signal),
    enabled: !externa && carteiras.length > 0,
    placeholderData: keepPreviousData,
  });
  const d = consulta.data?.data;

  return (
    <Pagina>
      <CabecalhoPagina
        titulo="Portfolio Analytics"
        descricao="Posição mais recente de carteiras sob gestão ou de uma carteira externa: alocação, emissores, vencimentos e duration, fluxo de caixa projetado, FGC, alertas e, para uma carteira onshore, a performance."
        dadosAte={externa ? undefined : consulta.data?.meta.dados_ate}
        fonte="Fibery (posições, ativos, política, contas, eventos) · ANBIMA (duration) · Warren One (performance)"
      />
      <BarraFiltros carregando={!externa && (lista.isFetching || consulta.isFetching)}>
        <GrupoFiltros titulo="Origem">
          <CampoOpcoes
            rotulo="Carteira"
            valor={filtros.origem}
            aoMudar={(origem) => definir({ origem })}
            opcoes={[
              { valor: 'gestao', rotulo: 'Sob gestão' },
              { valor: 'externa', rotulo: 'Externa (arquivo)' },
            ]}
          />
          {!externa && (
            <MultiSelect
              label="Carteiras"
              placeholder={carteiras.length ? '' : 'Selecione uma ou mais carteiras'}
              data={lista.data?.data.carteiras ?? []}
              value={carteiras}
              onChange={(v) => definir({ carteiras: v.join(',') })}
              searchable
              clearable
              w={LARGURA.selecao + 120}
            />
          )}
        </GrupoFiltros>
      </BarraFiltros>
      {externa ? (
        <Externa />
      ) : (
        <>
          <AvisosApi avisos={[consulta.data?.meta.avisos]} />
          {carteiras.length === 0 && <Alert color="blue">Selecione uma ou mais carteiras.</Alert>}
          {consulta.isError && (
            <Alert color="red">
              {mensagemErro(consulta.error, 'Não foi possível carregar as posições.')}
            </Alert>
          )}
          {carteiras.length > 0 && consulta.isPending && <Loader />}
          {d?.info && carteiras.length > 0 && <Alert color="blue">{d.info}</Alert>}
          {d?.abas && (
            <Resultado
              d={d}
              horizonte={filtros.horizonte}
              aoMudarHorizonte={(horizonte) => definir({ horizonte })}
              performance={{
                carteiras,
                aba: filtros.aba,
                aoMudarAba: (aba) => definir({ aba }),
                periodo: filtros.periodo,
                aoMudarPeriodo: (periodo) => definir({ periodo }),
              }}
            />
          )}
        </>
      )}
    </Pagina>
  );
}
